<div class="page-head">
  <div>
    <h1 class="page-title">
      Optimization
      <span class="pill pill-ready">Ready</span>
    </h1>
    <div class="page-sub">
      Black-Litterman · {{ forecastRows.length }} assets
      @if (portfolio?.currency?.code) {
        · Base currency {{ portfolio.currency.code }}
      }
    </div>
  </div>
  <div class="row" style="gap: 10px">
    <button class="btn btn-ghost btn-sm" type="button">Snapshot</button>
    <button class="btn btn-ghost btn-sm" type="button">Export</button>
  </div>
</div>

@if (!isLoading) {
  <div class="bento">
    <!-- Row 1: Settings hero (dates + chips + KPI pills + Optimize) -->
    <section class="card exp-col-12 settings-hero">
      <div>
        <div class="settings-left">
          <div class="field">
            <span class="field-label">Start Date</span>
            <input
              class="input"
              type="text"
              [value]="setting.startDate || '—'"
              readonly
            />
          </div>
          <div class="field">
            <span class="field-label">End Date</span>
            <input
              class="input"
              type="text"
              [value]="setting.endDate || '—'"
              readonly
            />
          </div>
          <div class="field rfr">
            <span class="field-label">Risk-Free</span>
            <input
              class="input"
              type="text"
              [value]="fmtPct(setting.rfr)"
              readonly
            />
          </div>
          <div class="chip-row" role="group" aria-label="Method">
            <span class="chip" [class.active]="setting.method === 'historical'"
              >Historical</span
            >
            <span
              class="chip"
              [class.active]="
                setting.method === 'implied' || setting.method === 'implied_ret'
              "
              >Implied</span
            >
            <span
              class="chip"
              [class.active]="
                setting.method === 'combined' ||
                setting.method === 'forecasted_returns'
              "
              >Combined</span
            >
          </div>
        </div>
      </div>
      <div class="row" style="gap: 14px">
        <div class="kpi-pills">
          <div class="kpi-pill">
            <span class="label">Expected return</span>
            <span class="value accent">{{ fmtPct(kpis.expReturn) }}</span>
          </div>
          <div class="kpi-pill">
            <span class="label">Volatility</span>
            <span class="value">{{ fmtPct(kpis.vol) }}</span>
          </div>
          <div class="kpi-pill">
            <span class="label">Sharpe</span>
            <span class="value">{{ fmtNum(kpis.sharpe) }}</span>
          </div>
          <div class="kpi-pill">
            <span class="label">VaR 95</span>
            <span class="value" [class.neg]="(kpis.varPct ?? 0) < 0">{{
              fmtPct(kpis.varPct)
            }}</span>
          </div>
        </div>
        <button class="btn btn-primary btn-lg" type="button">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
          </svg>
          Optimize
        </button>
      </div>
    </section>
    <!-- Row 2: Covariance / Correlation (exp-col-8) + Alti insights (exp-col-4) -->
    @if (corrMatrix.labels.length) {
      <section class="card exp-col-8">
        <div class="card-head">
          <div class="card-title">
            Covariance / Correlation
            <span class="hint"
              >· Top {{ corrMatrix.labels.length }} constituents</span
            >
          </div>
          <div class="card-tools">
            <div class="seg">
              <button
                type="button"
                [class.active]="covMode === 'matrix'"
                (click)="covMode = 'matrix'"
              >
                Matrix
              </button>
              <button
                type="button"
                [class.active]="covMode === 'graph'"
                (click)="covMode = 'graph'"
              >
                Graph
              </button>
            </div>
          </div>
        </div>
        @if (covMode === "matrix") {
          <app-exp-heatmap
            [labels]="corrMatrix.labels"
            [matrix]="corrMatrix.matrix"
            mode="divergent"
            [decimals]="2"
          ></app-exp-heatmap>
        }
        @if (covMode === "graph") {
          <app-exp-cov-graph
            [labels]="corrMatrix.labels"
            [weights]="topWeights"
            [matrix]="corrMatrix.matrix"
            [threshold]="0.2"
            [width]="620"
            [height]="360"
          ></app-exp-cov-graph>
        }
      </section>
    }
    <section class="card exp-col-4">
      <div class="card-head">
        <div class="card-title">
          <span class="alti-chip">Alti · Insights</span>
        </div>
      </div>
      <div class="insights-stack">
        @if (forecastRows.length) {
          <div class="insight">
            <div class="insight-head">
              <span class="insight-tag">Concentrated bet</span>
            </div>
            <p>
              <b>{{ forecastRows[0].ticker }}</b> carries the heaviest weight at
              <b>{{ fmtPct(forecastRows[0].weight) }}</b> with a combined
              forecast of <b>{{ fmtPct(forecastRows[0].forecast, 2) }}</b
              >.
            </p>
            <div class="insight-actions">
              <button class="btn btn-ghost btn-sm" type="button">
                Trim by 3pp
              </button>
              <button class="btn btn-text btn-sm" type="button">Explain</button>
            </div>
          </div>
        }
        <div class="insight">
          <div class="insight-head">
            <span class="insight-tag">Diversification</span>
          </div>
          <p>
            Combined expected return is
            <b>{{ fmtPct(combinedSummary.forecast, 2) }}</b
            >. Gap vs historical:
            <b>{{
              fmtPct(combinedSummary.forecast - combinedSummary.historical, 2)
            }}</b
            >.
          </p>
          <div class="insight-actions">
            <button class="btn btn-ghost btn-sm" type="button">
              Explain blend
            </button>
          </div>
        </div>
      </div>
    </section>
    <!-- Row 3: Implied Views full-width table -->
    @if (forecastRows.length) {
      <section class="card exp-col-12">
        <div class="card-head">
          <div class="card-title">
            Implied Views
            <span class="hint"
              >· Weights, implied returns, and deviation from market
              equilibrium</span
            >
          </div>
          <div class="card-tools">
            <button class="btn btn-ghost btn-sm" type="button">
              Export CSV
            </button>
          </div>
        </div>
        @if (forecastRows.length >= 3) {
          <div class="implied-narrative">
            <b>Top holdings:</b>
            @for (r of forecastRows.slice(0, 3); track r; let last = $last) {
              {{ r.ticker }} <strong>{{ fmtPct(r.weight) }}</strong> implies
              <span class="mono">{{ fmtPct(r.implied, 2) }}</span> (vs Mkt Eq
              <span class="mono">{{ fmtPct(r.marketEq, 2) }}</span
              >)
              @if (!last) {
                <span> · </span>
              }
            }
          </div>
        }
        <div style="overflow-x: auto">
          <table class="exp-table">
            <thead>
              <tr>
                <th class="asset">Asset</th>
                <th>Weight</th>
                <th>Historical</th>
                <th>Mkt Equilibrium</th>
                <th>Implied</th>
                <th>Combined</th>
                <th>Δ vs Mkt Eq</th>
              </tr>
            </thead>
            <tbody>
              @for (r of forecastRows; track r) {
                <tr>
                  <td class="asset">
                    <span class="ticker">{{ r.ticker }}</span>
                    <span>{{ r.name }}</span>
                  </td>
                  <td class="num">{{ fmtPct(r.weight, 1) }}</td>
                  <td class="num">{{ fmtPct(r.historical, 2) }}</td>
                  <td class="num">{{ fmtPct(r.marketEq, 2) }}</td>
                  <td
                    class="num"
                    style="color: var(--accent-2); font-weight: 600"
                  >
                    {{ fmtPct(r.implied, 2) }}
                  </td>
                  <td class="num">{{ fmtPct(r.forecast, 2) }}</td>
                  <td class="num cell-diverge">
                    <app-exp-divergent-cell
                      [value]="r.implied - r.marketEq"
                      [maxAbs]="maxForecastAbs"
                    ></app-exp-divergent-cell>
                  </td>
                </tr>
              }
            </tbody>
            <tfoot>
              <tr>
                <td class="asset">Portfolio blend</td>
                <td class="num">100.0%</td>
                <td class="num">{{ fmtPct(combinedSummary.historical, 2) }}</td>
                <td class="num">{{ fmtPct(combinedSummary.marketEq, 2) }}</td>
                <td class="num" style="color: var(--accent-2)">
                  {{ fmtPct(combinedSummary.implied, 2) }}
                </td>
                <td class="num">{{ fmtPct(combinedSummary.forecast, 2) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    }
    <!-- Row 4: My Views (exp-col-7) + Top weights hbar (exp-col-5) -->
    <section class="card exp-col-7">
      <div class="card-head">
        <div class="card-title">
          My Views <span class="hint">· Forward-looking opinions</span>
        </div>
        <div class="card-tools">
          <button class="btn btn-primary btn-sm" type="button">
            + Add view
          </button>
        </div>
      </div>
      <div class="view-grid">
        @for (v of views; track v) {
          @if (v.type !== "empty") {
            <div class="view-card">
              <div class="vc-head">
                <span class="vc-type-chip">{{ v.type }}</span>
                <span class="vc-name">{{ v.name }}</span>
              </div>
              <div class="vc-nums">
                <div class="vc-num-block">
                  <div class="label">Expected</div>
                  <div
                    class="value"
                    [class.pos]="(v.expectedReturn ?? 0) >= 0"
                    [class.neg]="(v.expectedReturn ?? 0) < 0"
                  >
                    {{ fmtPct(v.expectedReturn, 1) }}
                  </div>
                </div>
                @if (v.confidence) {
                  <div class="vc-num-block">
                    <div class="label">Confidence</div>
                    <div class="value">{{ v.confidence }}</div>
                  </div>
                }
              </div>
            </div>
          }
          @if (v.type === "empty") {
            <div class="view-card empty">+ Add a view</div>
          }
        }
      </div>
    </section>
    @if (hbarWeights.length) {
      <section class="card exp-col-5">
        <div class="card-head">
          <div class="card-title">
            Top weights <span class="hint">· Baseline allocation</span>
          </div>
        </div>
        <app-hbar-list
          [rows]="hbarWeights"
          [format]="fmtPct.bind(this)"
        ></app-hbar-list>
      </section>
    }
    <!-- Row 5: Constraints (exp-col-7) + Frontier (exp-col-5) -->
    <section class="card exp-col-7">
      <div class="card-head">
        <div class="card-title">
          Constraints <span class="hint">· Optimizer rules</span>
        </div>
        <button class="btn btn-text btn-sm" type="button">Edit all</button>
      </div>
      <table class="exp-table">
        <thead>
          <tr>
            <th class="asset">Scope</th>
            <th class="asset">Target</th>
            <th>Min</th>
            <th>Max</th>
          </tr>
        </thead>
        <tbody>
          @for (c of constraints; track c) {
            <tr>
              <td class="asset">{{ c.scope }}</td>
              <td class="asset">{{ c.target }}</td>
              <td class="num">{{ fmtPct(c.min, 0) }}</td>
              <td class="num">{{ fmtPct(c.max, 0) }}</td>
            </tr>
          }
        </tbody>
      </table>
    </section>
    @if (frontier.length) {
      <section class="card exp-col-5">
        <div class="card-head">
          <div class="card-title">
            Efficient Frontier <span class="hint">· Preview</span>
          </div>
          <div class="chip-row">
            <span class="chip active">Constrained</span>
            <span class="chip">Unconstrained</span>
          </div>
        </div>
        <app-exp-frontier-chart
          [frontier]="frontier"
          [unconstrained]="unconstrained"
          [assets]="frontierAssets"
          [portfolios]="frontierPortfolios"
          [width]="460"
          [height]="280"
        ></app-exp-frontier-chart>
      </section>
    }
    <!-- Row 6: Transition Map full-width -->
    @if (transitionRows.length) {
      <section class="card exp-col-12">
        <div class="card-head">
          <div class="card-title">
            Transition Map
            <span class="hint"
              >· How allocations shift from current to optimized</span
            >
          </div>
        </div>
        <app-exp-transition-map
          [rows]="transitionRows"
          [format]="fmtPct.bind(this)"
        ></app-exp-transition-map>
      </section>
    }
  </div>
}

@if (isLoading) {
  <div class="bento">
    <div class="card exp-col-12">
      <div class="shimmer" style="height: 120px"></div>
    </div>
    <div class="card exp-col-8">
      <div class="shimmer" style="height: 320px"></div>
    </div>
    <div class="card exp-col-4">
      <div class="shimmer" style="height: 320px"></div>
    </div>
    <div class="card exp-col-12">
      <div class="shimmer" style="height: 280px"></div>
    </div>
  </div>
}
